import {
  Button,
  ChakraAsyncSelect,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Stack,
  useBoolean,
  useDisclosure,
} from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { BaseFilterButton } from 'components/base-table/filters/base-filter-button';
import { usePageParams } from 'hooks/use-page-params';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';

type TInputs = {
  entityName: string;
  brandId: { value: string; label: string } | null;
};

const defaultValues: TInputs = {
  entityName: '',
  brandId: null,
};

export const EntityBrandFilterButton = () => {
  const [isActive, setIsActive] = useBoolean(false);
  const { isOpen, onClose, onOpen } = useDisclosure();
  const { getPageParams, setPageParams } = usePageParams();
  const params = getPageParams();

  useEffect(() => {
    if (params.entityName || params.brandId) setIsActive.on();
    else setIsActive.off();
  }, [params.entityName, params.brandId, setIsActive]);

  const { control, register, handleSubmit, formState, reset, setValue } = useForm<TInputs>({
    defaultValues: { ...defaultValues },
  });

  const loadBrandOptions = async (inputValue: string) => {
    try {
      const { data } = await http.get('ums/v1/crm/management/brands', {
        params: {
          page: 1,
          limit: 50,
          name: inputValue || undefined,
        },
      });
      return (data?.data || []).map((brand: any) => ({
        value: brand.id.toString(),
        label: brand.name,
      }));
    } catch (error) {
      console.error('Error loading brands:', error);
      return [];
    }
  };

  useEffect(() => {
    setValue('entityName', (params.entityName as string) ?? '');
    setValue(
      'brandId',
      params.brandId ? { value: String(params.brandId), label: `Brand ID: ${params.brandId}` } : null
    );
  }, [params.entityName, params.brandId, setValue]);

  const onSubmit = (data: TInputs) => {
    const newParams: any = { ...params };

    if (data.entityName.trim()) newParams.entityName = data.entityName.trim();
    else delete newParams.entityName;

    if (data.brandId?.value) newParams.brandId = data.brandId.value;
    else delete newParams.brandId;

    setPageParams(newParams);
    onClose();
    reset(data);
  };

  const handleClose = () => {
    onClose();
    setValue('entityName', (params.entityName as string) ?? '');
    setValue(
      'brandId',
      params.brandId ? { value: String(params.brandId), label: `Brand ID: ${params.brandId}` } : null
    );
  };

  const handleClear = () => {
    setValue('entityName', '');
    setValue('brandId', null);
    const newParams: any = { ...params };
    delete newParams.entityName;
    delete newParams.brandId;
    setPageParams(newParams);
    onClose();
  };

  const handleDisableFilterButton = () => {
    const isDisabled = Object.keys(formState.dirtyFields).length ? false : true;
    return isDisabled;
  };

  return (
    <BaseFilterButton
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={handleClose}
      isActive={isActive}
      formComponent={
        <Stack as='form' spacing='lg' p='md' pt='lg' onSubmit={handleSubmit(onSubmit)}>
          <FormControl>
            <FormLabel>Entity Name</FormLabel>
            <Input size='sm' placeholder='Search by entity name...' {...register('entityName')} />
          </FormControl>
          <FormControl>
            <FormLabel>Brand</FormLabel>
            <Controller
              control={control}
              name='brandId'
              render={({ field: { onChange, onBlur, value, ref } }) => (
                <ChakraAsyncSelect
                  ref={ref}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  loadOptions={loadBrandOptions}
                  placeholder='Search and select brand...'
                  isClearable
                  noOptionsMessage={({ inputValue }) => (inputValue ? 'No brands found' : 'Type to search brands')}
                  loadingMessage={() => 'Loading brands...'}
                  cacheOptions
                  defaultOptions
                />
              )}
            />
          </FormControl>

          <HStack justify='flex-end'>
            <Button variant='outline' colorScheme='gray' onClick={handleClear}>
              Clear
            </Button>
            <Button variant='outline' colorScheme='red' onClick={handleClose}>
              Cancel
            </Button>
            <Button variant='solid' colorScheme='red' type='submit' disabled={handleDisableFilterButton()}>
              Filter
            </Button>
          </HStack>
        </Stack>
      }
    />
  );
};
